import React, { useCallback, useEffect, useState } from 'react';
import { ArrowPathIcon, ExclamationTriangleIcon, MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import collectionsService from '../../services/collectionsService';
import { errText } from '../../services/scheduleService';
import { rands } from '../../components/billing/TariffLines';
import AccountPanel from './AccountPanel';

const BUCKETS = [['current', 'Current'], ['d30', '31–60'], ['d60', '61–90'], ['d90', '91–120'], ['d120', '120+']];
const OWED = [['patient', 'Patients owe'], ['scheme', 'With medical aids'], ['all', 'Everything']];
const fmtDay = (d) => (d ? new Date(String(d).length === 10 ? `${d}T12:00:00` : d).toLocaleDateString('en-ZA', { day: 'numeric', month: 'short' }) : '—');
const amt = (n) => (n > 0.005 ? rands(n) : <span className="text-slate-300">—</span>);

const Tile = ({ label, value, sub, warn, onClick, active }) => (
  <button onClick={onClick} className={`rounded-xl border bg-white px-4 py-3 text-left transition ${active ? 'border-brand-navy ring-2 ring-brand-navy/10' : 'border-slate-200 hover:border-slate-300'}`}>
    <div className="text-xs font-semibold text-slate-500">{label}</div>
    <div className="mt-1 text-2xl font-semibold text-slate-900">{value}</div>
    <div className={`mt-0.5 flex items-center gap-1 text-xs ${warn ? 'font-semibold text-red-700' : 'text-slate-500'}`}>
      {warn && <ExclamationTriangleIcon className="h-3.5 w-3.5" />}{sub}
    </div>
  </button>
);

/**
 * Debtors & Collections — who owes the practice what, and for how long.
 * Patients: co-payments, shortfalls and private accounts, aged from the date of
 * service; medical aids: claims not yet paid. Open a patient for their account:
 * record payments, write-offs, statements and the follow-up history.
 */
const Debtors = () => {
  const [owedBy, setOwedBy] = useState('patient');
  const [q, setQ] = useState('');
  const [dueOnly, setDueOnly] = useState(false);
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [open, setOpen] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setData(await collectionsService.debtors({ owedBy, q }));
      setError('');
    } catch (e) {
      setError(errText(e));
    } finally {
      setLoading(false);
    }
  }, [owedBy, q]);
  useEffect(() => {
    const t = setTimeout(load, q ? 250 : 0);
    return () => clearTimeout(t);
  }, [load, q]);

  const t = data?.totals;
  const rows = (data?.rows || []).filter((r) => !dueOnly || r.follow_up_due);
  const shownTotals = owedBy === 'all' ? t?.all : t?.[owedBy];

  return (
    <div className="mx-auto max-w-7xl p-8">
      <div className="mb-5 flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-brand-deep">Debtors &amp; collections</h1>
          <p className="mt-1 text-slate-500">What patients and medical aids still owe, aged from the date of service.</p>
        </div>
        <button onClick={load} disabled={loading} title="Refresh" className="rounded-lg border border-slate-300 bg-white p-2 text-slate-500 hover:bg-slate-50">
          <ArrowPathIcon className={`h-5 w-5 ${loading ? 'animate-spin' : ''}`} />
        </button>
      </div>

      {error && <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{error}</div>}

      {t && (
        <div className="mb-5 grid grid-cols-2 gap-3 lg:grid-cols-4">
          <Tile label="Patients owe" value={rands(t.patient.total)} sub={`${rands(t.patient.d90 + t.patient.d120)} over 90 days`} warn={t.patient.d90 + t.patient.d120 > 0} active={owedBy === 'patient' && !dueOnly} onClick={() => { setOwedBy('patient'); setDueOnly(false); }} />
          <Tile label="With medical aids" value={rands(t.scheme.total)} sub={`${rands(t.scheme.d60 + t.scheme.d90 + t.scheme.d120)} over 60 days`} active={owedBy === 'scheme'} onClick={() => { setOwedBy('scheme'); setDueOnly(false); }} />
          <Tile label="Total outstanding" value={rands(t.all.total)} sub="Patients and medical aids" active={owedBy === 'all' && !dueOnly} onClick={() => { setOwedBy('all'); setDueOnly(false); }} />
          <Tile label="Follow-ups due" value={data.follow_ups_due} sub="Today or overdue" warn={data.follow_ups_due > 0} active={dueOnly} onClick={() => { setOwedBy('all'); setDueOnly(true); }} />
        </div>
      )}

      {t && (
        <section className="mb-5 overflow-hidden rounded-xl border border-slate-200 bg-white">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-slate-50 text-left text-[11px] uppercase tracking-wide text-slate-500">
                <th className="px-4 py-2 font-semibold">Age analysis</th>
                {BUCKETS.map(([k, l]) => <th key={k} className="px-3 py-2 text-right font-semibold">{k === 'current' ? l : `${l} days`}</th>)}
                <th className="px-4 py-2 text-right font-semibold">Total</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 tabular-nums">
              {[['Patients', t.patient], ['Medical aids', t.scheme]].map(([label, a]) => (
                <tr key={label}>
                  <td className="px-4 py-2 font-semibold text-slate-700">{label}</td>
                  {BUCKETS.map(([k]) => <td key={k} className="px-3 py-2 text-right text-slate-700">{amt(a[k])}</td>)}
                  <td className="px-4 py-2 text-right font-semibold text-slate-900">{rands(a.total)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      )}

      <div className="mb-3 flex flex-wrap items-center gap-2">
        <div className="flex rounded-lg border border-slate-200 bg-white p-0.5">
          {OWED.map(([k, l]) => (
            <button key={k} onClick={() => { setOwedBy(k); setDueOnly(false); }} className={`rounded-md px-3 py-1.5 text-sm font-semibold ${owedBy === k && !dueOnly ? 'bg-brand-navy text-white' : 'text-slate-600 hover:bg-slate-50'}`}>{l}</button>
          ))}
        </div>
        <label className="flex items-center gap-1.5 text-sm text-slate-600"><input type="checkbox" checked={dueOnly} onChange={(e) => setDueOnly(e.target.checked)} /> Follow-ups due only</label>
        <div className="relative ml-auto">
          <MagnifyingGlassIcon className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Patient name or cell" className="w-64 rounded-lg border border-slate-300 py-1.5 pl-8 pr-3 text-sm" />
        </div>
      </div>

      <section className="overflow-x-auto rounded-xl border border-slate-200 bg-white">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-slate-100 bg-slate-50 text-left text-[11px] uppercase tracking-wide text-slate-500">
              <th className="px-4 py-2 font-semibold">Patient</th>
              {BUCKETS.map(([k, l]) => <th key={k} className="px-3 py-2 text-right font-semibold">{l}</th>)}
              <th className="px-3 py-2 text-right font-semibold">Total</th>
              <th className="px-3 py-2 font-semibold">Last paid</th>
              <th className="px-3 py-2 font-semibold">Statement</th>
              <th className="px-4 py-2 font-semibold">Follow up</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100 tabular-nums">
            {rows.map((r) => (
              <tr key={r.patient_id} onClick={() => setOpen(r.patient_id)} className="cursor-pointer hover:bg-slate-50">
                <td className="px-4 py-2.5">
                  <div className="font-semibold text-slate-800">{r.name}</div>
                  <div className="text-xs text-slate-500">
                    {r.invoices} invoice{r.invoices === 1 ? '' : 's'}{r.cell_phone ? ` · ${r.cell_phone}` : ''}
                    {owedBy !== 'patient' && r.schemes.length ? ` · ${r.schemes.join(', ')}` : ''}
                  </div>
                </td>
                {BUCKETS.map(([k]) => <td key={k} className={`px-3 py-2.5 text-right ${k === 'd90' || k === 'd120' ? 'text-red-700' : 'text-slate-700'}`}>{amt(r[k])}</td>)}
                <td className="px-3 py-2.5 text-right font-semibold text-slate-900">{rands(r.total)}</td>
                <td className="px-3 py-2.5 text-xs text-slate-500">{fmtDay(r.last_patient_payment)}</td>
                <td className="px-3 py-2.5 text-xs text-slate-500">{fmtDay(r.last_statement)}</td>
                <td className="px-4 py-2.5 text-xs">
                  {r.next_follow_up
                    ? <span className={r.follow_up_due ? 'font-semibold text-red-700' : 'text-slate-600'}>{r.follow_up_due ? 'Due ' : ''}{fmtDay(r.next_follow_up)}</span>
                    : <span className="text-slate-300">—</span>}
                </td>
              </tr>
            ))}
            {data && rows.length === 0 && <tr><td colSpan={10} className="px-4 py-8 text-center text-slate-400">{dueOnly ? 'No follow-ups due.' : 'Nobody owes anything here.'}</td></tr>}
            {!data && !error && <tr><td colSpan={10} className="px-4 py-8 text-center text-slate-400">Loading…</td></tr>}
          </tbody>
          {rows.length > 1 && shownTotals && !dueOnly && !q && (
            <tfoot className="border-t border-slate-200 font-semibold tabular-nums">
              <tr>
                <td className="px-4 py-2 text-slate-600">Total</td>
                {BUCKETS.map(([k]) => <td key={k} className="px-3 py-2 text-right text-slate-800">{amt(shownTotals[k])}</td>)}
                <td className="px-3 py-2 text-right text-slate-900">{rands(shownTotals.total)}</td>
                <td colSpan={3} />
              </tr>
            </tfoot>
          )}
        </table>
      </section>

      {owedBy === 'scheme' && data?.schemes?.length > 0 && (
        <section className="mt-5 overflow-hidden rounded-xl border border-slate-200 bg-white">
          <div className="border-b border-slate-100 px-4 py-2.5 font-bold text-slate-800">By medical aid</div>
          <table className="w-full text-sm tabular-nums">
            <tbody className="divide-y divide-slate-100">
              {data.schemes.map((s) => (
                <tr key={s.scheme_name}>
                  <td className="px-4 py-2 text-slate-700">{s.scheme_name} <span className="text-xs text-slate-400">· {s.invoices} claim{s.invoices === 1 ? '' : 's'}</span></td>
                  {BUCKETS.map(([k]) => <td key={k} className="px-3 py-2 text-right text-slate-700">{amt(s[k])}</td>)}
                  <td className="px-4 py-2 text-right font-semibold">{rands(s.total)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      )}

      {open && <AccountPanel patientId={open} onClose={() => setOpen(null)} onChanged={load} />}
    </div>
  );
};

export default Debtors;
